import { useEffect, useState, useCallback } from "react";
import { getSimuladorTeams, getStandings } from "../../services/getSimuladorTeams";
import { getEvents } from "../../services/getEvents";
import WidgetContainer from "../reusablesComponents/containerWidget";
import TableResultsSimulator from "./tableResultsSimulator/tableResultsSimulator";
import MatchesByPhase from "./matchesByPhase/matchesByPhase";
import SimulatorButtons from "./buttonsSimulator/simulatorButtons";
import CarrouselConfrontation from "./carrouselConfrontation/carrouselConfrontation";
import "./simulatorLiguilla.sass";

export const IMG_BASE = "https://omo.akamai.opta.net/image.php?secure=true&h=omo.akamai.opta.net&sport=football&entity=team&description=badges&dimensions=65&id=";

export default function SimulatorLiguilla() {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [originalStandings, setOriginalStandings] = useState(null);
  const [initialStandings, setInitialStandings] = useState(null); // Tabla inicial sin cambios
  const [results, setResults] = useState(null); // Tabla recalculada con cambios

  /**
   * Efecto que se ejecuta al montar el componente para cargar los datos iniciales
   * Obtiene la tabla de posiciones desde getStandings() y los eventos reales
   * Luego genera los partidos del simulador
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener standings desde getStandings()
        const standingsResult = await getStandings();
        
        // Obtener eventos reales de la API con la nueva estructura
        const eventsResult = await getEvents();
        
        // Extraer eventos de la nueva estructura de matchdays
        let allEvents = [];
        if (eventsResult.data && eventsResult.data.matchdays && Array.isArray(eventsResult.data.matchdays)) {
          allEvents = eventsResult.data.matchdays.flatMap(matchday => matchday.events || []);
        } else if (Array.isArray(eventsResult.data)) {
          // Fallback: si es array directo
          allEvents = eventsResult.data;
        }
        
        if (standingsResult.statusCode === 200 && standingsResult.data) {
          // Extraer el array de rankings de la estructura anidada
          // La API devuelve: data.soccerStages[0].division[0].ranking
          const allStandings = standingsResult.data.soccerStages?.[0]?.division?.[0]?.ranking || [];
          
          // Validar que hay standings disponibles
          if (!Array.isArray(allStandings) || allStandings.length === 0) {
            console.error("No standings disponibles o estructura inválida", allStandings);
            setLoading(false);
            return;
          }
          
          setOriginalStandings(allStandings);
          
          // Generar partidos basados en los eventos reales
          const generatedMatches = generateMatches(allStandings, allEvents);
          setMatches(generatedMatches);
                    
          // Guardar standings inicial sin cambios para mostrar en TableResultsSimulator
          const initialStandingsWithDefault = allStandings.map((team, index) => ({
            ...team,
            rank: index + 1
          }));
          setInitialStandings(initialStandingsWithDefault);
          
          // Calcular tabla inicial considerando partidos in-progress
          const standingsWithInProgress = calculateStandingsWithInProgress(allStandings, generatedMatches);
          
          // Mostrar tabla inicial con partidos in-progress ya procesados
          setResults({ finalStandings: standingsWithInProgress });
        }
      } catch (error) {
        console.error("Error al obtener datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /**
   * Calcula puntos y estadísticas de un partido individual
   * Actualiza directamente los equipos con los resultados
   * 
   * @param {Object} homeTeam - Equipo local (mutated)
   * @param {Object} awayTeam - Equipo visitante (mutated)
   * @param {number} homeGoals - Goles del equipo local
   * @param {number} awayGoals - Goles del equipo visitante
   * @param {boolean} shouldCapMatches - Si true, limita matchesPlayed a 17
   */
  const applyMatchResult = (homeTeam, awayTeam, homeGoals, awayGoals, shouldCapMatches = false) => {
    // SUMAR goles
    homeTeam.goalsFor += homeGoals;
    homeTeam.goalsAgainst += awayGoals;
    awayTeam.goalsFor += awayGoals;
    awayTeam.goalsAgainst += homeGoals;

    // ACTUALIZAR diferencia de goles
    homeTeam.goalDifference = homeTeam.goalsFor - homeTeam.goalsAgainst;
    awayTeam.goalDifference = awayTeam.goalsFor - awayTeam.goalsAgainst;

    // Función auxiliar para incrementar matchesPlayed
    const incrementMatchesPlayed = (team) => {
      if (shouldCapMatches) {
        if (team.matchesPlayed < 17) team.matchesPlayed += 1;
      } else {
        team.matchesPlayed += 1;
      }
    };

    // SUMAR puntos según resultado
    if (homeGoals > awayGoals) {
      // Victoria local
      homeTeam.matchesWon += 1;
      incrementMatchesPlayed(homeTeam);
      homeTeam.points += 3;
      awayTeam.matchesLost += 1;
      incrementMatchesPlayed(awayTeam);
    } else if (homeGoals < awayGoals) {
      // Victoria visitante
      awayTeam.matchesWon += 1;
      incrementMatchesPlayed(awayTeam);
      awayTeam.points += 3;
      homeTeam.matchesLost += 1;
      incrementMatchesPlayed(homeTeam);
    } else {
      // Empate (incluye 0-0)
      homeTeam.matchesDrawn += 1;
      incrementMatchesPlayed(homeTeam);
      homeTeam.points += 1;
      awayTeam.matchesDrawn += 1;
      incrementMatchesPlayed(awayTeam);
      awayTeam.points += 1;
    }
  };

  /**
   * Calcula standings desde cero filtrando matches según criterio
   * Función genérica reutilizable para múltiples casos de cálculo
   * 
   * @param {Array} standings - Array original de equipos
   * @param {Array} matchList - Array de partidos a procesar
   * @param {Function} filterFn - Función que retorna true si el match debe procesarse
   * @param {boolean} shouldCapMatches - Si true, limita matchesPlayed a 17
   * @returns {Array} Array de equipos con standings actualizados y ordenados
   */
  const calculateStandingsFromMatches = (standings, matchList, filterFn, shouldCapMatches = false) => {
    try {
      // Crear copia de los standings originales
      const updatedStandings = standings.map(team => ({
        ...team
      }));

      // Procesar partidos que cumplen el criterio del filtro
      matchList.forEach(match => {
        if (!filterFn(match)) return;

        const homeTeam = updatedStandings.find(t => t.contestantId === match.HomeTeamId);
        const awayTeam = updatedStandings.find(t => t.contestantId === match.AwayTeamId);

        if (!homeTeam || !awayTeam) return;

        const homeGoals = match.homeGoals || 0;
        const awayGoals = match.awayGoals || 0;

        applyMatchResult(homeTeam, awayTeam, homeGoals, awayGoals, shouldCapMatches);
      });

      // Ordenar por: Puntos → Diferencia de Goles → Goles a Favor
      updatedStandings.sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
        return b.goalsFor - a.goalsFor;
      });

      // Asignar ranking final
      return updatedStandings.map((team, index) => ({
        ...team,
        rank: index + 1
      }));
    } catch (error) {
      console.error('Error al calcular standings:', error);
      return standings;
    }
  };

  /**
   * Calcula la tabla de posiciones inicial considerando partidos in-progress
   * Los partidos in-progress se cuentan en la tabla con sus valores reales
   * 
   * @param {Array} standings - Array original de equipos
   * @param {Array} matchList - Array de partidos (incluye in-progress)
   * @returns {Array} Array de equipos con in-progress ya procesados
   */
  const calculateStandingsWithInProgress = (standings, matchList) => {
    return calculateStandingsFromMatches(
      standings,
      matchList,
      match => match.status === 'clarosports-in-progress-game'
    );
  };

  /**
   * Genera partidos desde los eventos con la nueva estructura de matchdays
   * Cada evento ahora tiene toda la información necesaria
   * 
   * @param {Array} standings - Array de todos los equipos ordenados por posición
   * @param {Array} events - Array de eventos con estructura completa
   * @returns {Array} Array con todos los partidos enriquecidos con datos de standings
   */
  const generateMatches = (standings, events = []) => {
    const matches = [];
    
    events.forEach((event, index) => {
      // Buscar equipos en standings por su contestantId
      const homeTeam = standings.find(t => t.contestantId === event.id_local);
      const awayTeam = standings.find(t => t.contestantId === event.id_visita);

      // Si encontramos ambos equipos
      if (homeTeam && awayTeam) {
        // Lógica de scores según la clase del partido:
        // - clarosports-next-game: dejar vacío (editable por usuario)
        // - clarosports-in-progress-game: usar scores reales (editable, se cuenta en tabla)
        // - clarosports-finished-game: usar scores reales (NO editable, NO se cuenta en tabla)
        const isNextGame = event.class === 'clarosports-next-game';
        const isInProgress = event.class === 'clarosports-in-progress-game';
        const isFinished = event.class === 'clarosports-finished-game';

        let homeGoalsValue, awayGoalsValue;
        
        if (isNextGame) {
          // next-game: dejar vacío
          homeGoalsValue = null;
          awayGoalsValue = null;
        } else if (isInProgress || isFinished) {
          // in-progress y finished: usar scores reales
          homeGoalsValue = parseInt(event.left_score) || 0;
          awayGoalsValue = parseInt(event.right_score) || 0;
        }

        const match = {
          id: index + 1,
          homeTeam: event.left_short_name || homeTeam.contestantShortName,
          awayTeam: event.right_short_name || awayTeam.contestantShortName,
          homeGoals: homeGoalsValue,  // Vacío si es next-game, sino usa score real
          awayGoals: awayGoalsValue,  // Vacío si es next-game, sino usa score real
          HomeTeamId: homeTeam.contestantId,
          AwayTeamId: awayTeam.contestantId,
          homeTeamImg: event.left_image || homeTeam.image,
          awayTeamImg: event.right_image || awayTeam.image,
          // Datos reales del evento
          eventId: event.id_juego,
          venueName: event.venue_name,
          eventPhase: event.phase,
          eventDate: event.date,
          eventTime: event.period_title,
          estatus: event.class,
          hasRealData: true,
          // Nuevos campos para bloqueo y en progreso
          status: event.class,
          isBlocked: event.class === 'clarosports-finished-game',
          isInProgress: event.class === 'clarosports-in-progress-game',
          realLeftScore: parseInt(event.left_score) || 0,
          realRightScore: parseInt(event.right_score) || 0,
          // Campos para fase y jornada
          fase: event.phase || 'General',
          jornada: event.phase ? event.phase.split(' ').pop() : '1',
          // Campo current del matchday
          current: event.current || false,
          // Fecha y hora UTC del partido
          utc_date: event.utc_date || event.date
        };
      
        matches.push(match);
      } else {
        console.warn(`⚠️ Evento ${index + 1}: No se encontraron equipos para ${event.left_name} vs ${event.right_name}`);
      }
    });
    return matches;
  };

  /**
   * Actualiza el marcador de un partido específico
   * SOLO cuando AMBOS scores están completos, se actualiza la tabla
   * SOLO se modifican los equipos del partido actualizado
   * @param {number} matchId - ID del partido a actualizar
   * @param {string} team - Equipo a actualizar ('home' o 'away')
   * @param {string} score - Nuevo marcador como string del input
   */
  const updateScore = useCallback((matchId, team, score) => {
    // Encontrar el partido que se está editando
    const matchToUpdate = matches.find(m => m.id === matchId);
    if (!matchToUpdate) return;

    // Convertir score a número o null si está vacío
    const numScore = score === '' ? null : parseInt(score);

    // Actualizar el estado de matches
    const newMatches = matches.map(match =>
      match.id === matchId
        ? { ...match, [`${team}Goals`]: numScore }
        : match
    );
    setMatches(newMatches);
    
    // Obtener el partido actualizado
    const updatedMatch = newMatches.find(m => m.id === matchId);
    
    // SOLO recalcular si ambos scores tienen valores (no null)
    const bothScoresComplete = updatedMatch.homeGoals !== null && updatedMatch.awayGoals !== null;
    
    if (bothScoresComplete) {
      // Actualizar SOLO los equipos del partido modificado
      updateAffectedTeamsOnly(originalStandings, results?.finalStandings || initialStandings, newMatches);
    } else {
      // Si alguno está vacío, mostrar tabla inicial
      setResults({ finalStandings: initialStandings });
    }
  }, [matches, originalStandings, results, initialStandings]);

  /**
   * Actualiza SOLO los equipos afectados por partidos completados
   * Considera tanto partidos next-game editados como in-progress
   * 
   * Procesa partidos que:
   * - NO están finalizados (status !== 'clarosports-finished-game')
   * - Tienen AMBOS scores (homeGoals !== null AND awayGoals !== null)
   * - Incluye: clarosports-next-game (editados por usuario) y clarosports-in-progress-game (datos reales)
   * - Excluye: clarosports-finished-game (no se modifican en la tabla)
   * 
   * @param {Array} originalStandings - Standings originales del servicio
   * @param {Array} currentStandings - Standings actuales (con cambios previos, NO USADO)
   * @param {Array} allMatches - Todos los partidos actuales
   */
  const updateAffectedTeamsOnly = (originalStandings, currentStandings, allMatches) => {
    const finalStandings = calculateStandingsFromMatches(
      originalStandings,
      allMatches,
      match => 
        match.homeGoals !== null && 
        match.awayGoals !== null && 
        match.status !== 'clarosports-finished-game',
      true // shouldCapMatches = true (limita a 17 partidos jugados)
    );

    setResults({ finalStandings });
  };

  const limpiar = useCallback(() => {
    const clearedMatches = matches.map(match => {
      // Solo limpiar si es next-game (editable)
      // in-progress y finished-game mantienen sus scores
      if (match.status === 'clarosports-next-game') {
        return {
          ...match,
          homeGoals: null,
          awayGoals: null
        };
      }
      return match;
    });
    setMatches(clearedMatches);
    
    // Mostrar tabla inicial
    setResults({ finalStandings: initialStandings });
  }, [matches, initialStandings]);

  const aleatorio = useCallback(() => {
    // Solo aplicar scores aleatorios a partidos next-game
    const newMatches = matches.map(match => {
      // Solo generar scores aleatorios si es next-game (editable)
      // in-progress y finished-game mantienen sus scores reales
      if (match.status === 'clarosports-next-game') {
        return {
          ...match,
          homeGoals: Math.floor(Math.random() * 4),  // Valores: 0, 1, 2, 3
          awayGoals: Math.floor(Math.random() * 4)   // Valores: 0, 1, 2, 3
        };
      }
      return match;
    });
    setMatches(newMatches);
    
    // Recalcular tabla con todos los cambios
    updateAffectedTeamsOnly(originalStandings, initialStandings, newMatches);
  }, [matches, originalStandings, initialStandings]);


  return (
    <WidgetContainer loading={loading} className="container__simulador-liguilla" heightLoading={800}>
      <div className="simulador-liguilla">
        <div className="simulador-liguilla__header">
          <SimulatorButtons onAleatorio={aleatorio} onLimpiar={limpiar} />
        </div>
        <div className="container-global-confrontations">
          <div className="global-confrontations-title">
          <p>Posibles Cuartos de final</p>
          </div>
          <CarrouselConfrontation data={results} />
        </div>
        <div className="box-liguilla_matches">
          <div className="simulador-liguilla__matches">
            <MatchesByPhase
            matches={matches}
            updateScore={updateScore}
            />
          </div>
          <TableResultsSimulator results={results} />
        </div>
      </div>

    </WidgetContainer>
  );
}